// src/pages/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 80px 0 60px;
  background: linear-gradient(135deg, #f5f5f7 0%, #ffffff 50%, #f5f5f7 100%);
  border-radius: 24px;
  margin-bottom: 80px;
  position: relative;

  @media (max-width: 768px) {
    padding: 50px 0 40px;
    margin-bottom: 60px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 0 30px;
    margin-bottom: 40px;
    border-radius: 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 700;
  background: linear-gradient(45deg, #1d1d1f, #424245, #1d1d1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 24px;
  letter-spacing: -1px;
  line-height: 1.1;
  padding: 0 20px;

  @media (max-width: 1024px) {
    font-size: 56px;
  }

  @media (max-width: 768px) {
    font-size: 42px;
    margin-bottom: 20px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 16px;
    padding: 0 12px;
    letter-spacing: -0.5px;
  }

  @media (max-width: 360px) {
    font-size: 28px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: #86868b;
  font-weight: 400;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.4;
  padding: 0 30px;

  @media (max-width: 1024px) {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 32px;
    padding: 0 20px;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 24px;
    padding: 0 16px;
  }

  @media (max-width: 360px) {
    font-size: 16px;
    padding: 0 12px;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 0 40px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
    gap: 12px;
  }

  @media (max-width: 360px) {
    padding: 0 16px;
  }
`;

const PrimaryButton = styled(Link)`
  padding: 16px 32px;
  background: linear-gradient(135deg, #0071e3, #0077ed);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  min-width: 200px;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 113, 227, 0.3);
    background: linear-gradient(135deg, #0077ed, #0082f3);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    font-size: 17px;
    padding: 14px 24px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 12px 20px;
    max-width: 100%;
  }
`;

const SecondaryButton = styled(Link)`
  padding: 16px 32px;
  background: transparent;
  color: #0071e3;
  text-decoration: none;
  border: 2px solid #0071e3;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 200px;
  text-align: center;

  &:hover {
    background: rgba(0, 113, 227, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    font-size: 17px;
    padding: 14px 24px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 12px 20px;
    max-width: 100%;
  }
`;

const ExternalButton = styled.a`
  padding: 16px 32px;
  background: linear-gradient(135deg, #30d158, #34c759);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  min-width: 200px;
  text-align: center;
  display: inline-block;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(52, 199, 89, 0.3);
    background: linear-gradient(135deg, #34c759, #40d157);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    font-size: 17px;
    padding: 14px 24px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 12px 20px;
    max-width: 100%;
  }
`;

const FeaturesSection = styled.div`
  margin-bottom: 80px;

  @media (max-width: 768px) {
    margin-bottom: 60px;
  }

  @media (max-width: 480px) {
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 60px;
  letter-spacing: -0.5px;
  padding: 0 20px;

  @media (max-width: 1024px) {
    font-size: 42px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 32px;
    padding: 0 12px;
  }

  @media (max-width: 360px) {
    font-size: 28px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    gap: 16px;
    margin-bottom: 32px;
  }
`;

const FeatureCard = styled(Link)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px 32px;
  text-decoration: none;
  color: inherit;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 14px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const PromotionCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 14px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0071e3, #0077ed);
  border-radius: 20px;
  color: white;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 40px;
    margin-bottom: 20px;
    border-radius: 18px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    font-size: 32px;
    margin-bottom: 16px;
    border-radius: 16px;
  }
`;

const PromotionIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #30d158, #34c759);
  border-radius: 20px;
  color: white;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 40px;
    margin-bottom: 20px;
    border-radius: 18px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    font-size: 32px;
    margin-bottom: 16px;
    border-radius: 16px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 14px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const FeatureDescription = styled.p`
  color: #515154;
  font-size: 17px;
  line-height: 1.5;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 14px;
  }
`;

const FeatureHighlight = styled.span`
  color: #0071e3;
  font-weight: 600;
  font-size: 15px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PromotionHighlight = styled.span`
  color: #30d158;
  font-weight: 600;
  font-size: 15px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CountriesSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 50px 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 40px 30px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 32px 20px;
    border-radius: 14px;
  }

  @media (max-width: 360px) {
    padding: 24px 16px;
  }
`;

const CountriesTitle = styled.h3`
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin-bottom: 16px;
  }
`;

const CountriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    max-width: 400px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
    max-width: 280px;
  }
`;

const CountryTag = styled.span`
  padding: 12px 20px;
  background: rgba(0, 113, 227, 0.1);
  color: #0071e3;
  border-radius: 12px;
  font-weight: 500;
  font-size: 16px;

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 10px;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin: 80px 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  @media (max-width: 768px) {
    margin: 60px 0;
    gap: 20px;
  }

  @media (max-width: 480px) {
    margin: 40px 0;
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px 20px;

  @media (max-width: 768px) {
    padding: 25px 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  background: linear-gradient(135deg, #0071e3, #0077ed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const StatLabel = styled.div`
  color: #86868b;
  font-size: 16px;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PromotionSection = styled.div`
  margin: 80px 0;

  @media (max-width: 768px) {
    margin: 60px 0;
  }

  @media (max-width: 480px) {
    margin: 40px 0;
  }
`;

const Home = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>EEBootcamp Power Utility Career Board</HeroTitle>
        <HeroSubtitle>
          Your comprehensive platform for launching and advancing your career in the power utility industry. 
          Discover opportunities, build skills, and connect with leading companies worldwide.
        </HeroSubtitle>
        <CTAContainer>
          <PrimaryButton to="/skills-mapper">
            Start Your Journey
          </PrimaryButton>
          <SecondaryButton to="/company-directory">
            Explore Companies
          </SecondaryButton>
        </CTAContainer>
      </HeroSection>

      <StatsSection>
        <StatCard>
          <StatNumber>100+</StatNumber>
          <StatLabel>Companies</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>35+</StatNumber>
          <StatLabel>Job Roles</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>100+</StatNumber>
          <StatLabel>Interview Questions</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>6</StatNumber>
          <StatLabel>Countries Covered</StatLabel>
        </StatCard>
      </StatsSection>

      <FeaturesSection>
        <SectionTitle>Free Career Tools & Resources</SectionTitle>
        <FeaturesGrid>
          <FeatureCard to="/company-directory">
            <FeatureIcon>🏢</FeatureIcon>
            <FeatureTitle>Company Directory</FeatureTitle>
            <FeatureDescription>
              Access career pages of utility and EPC companies across multiple countries. 
              Find your next opportunity with direct links to company career portals.
            </FeatureDescription>
            <FeatureHighlight>Explore Companies →</FeatureHighlight>
          </FeatureCard>

          <FeatureCard to="/role-explorer">
            <FeatureIcon>🔍</FeatureIcon>
            <FeatureTitle>Role Explorer</FeatureTitle>
            <FeatureDescription>
              Browse detailed job profiles with comprehensive information about required skills, 
              typical responsibilities, and average salary ranges for each position.
            </FeatureDescription>
            <FeatureHighlight>Explore Roles →</FeatureHighlight>
          </FeatureCard>

          <FeatureCard to="/interview-questions">
            <FeatureIcon>💼</FeatureIcon>
            <FeatureTitle>Interview Question Bank</FeatureTitle>
            <FeatureDescription>
              Prepare with our extensive collection of behavioral and technical interview questions 
              specifically tailored for the utility sector. Search and filter by category.
            </FeatureDescription>
            <FeatureHighlight>Practice Questions →</FeatureHighlight>
          </FeatureCard>

          <FeatureCard to="/skills-mapper">
            <FeatureIcon>🗺️</FeatureIcon>
            <FeatureTitle>Skills Mapper</FeatureTitle>
            <FeatureDescription>
              Input your skills and discover which utility industry roles are the best fit. 
              Visualize career paths and identify skills gaps to focus your development.
            </FeatureDescription>
            <FeatureHighlight>Map Your Skills →</FeatureHighlight>
          </FeatureCard>
        </FeaturesGrid>

        <CountriesSection>
          <CountriesTitle>Global Company Coverage</CountriesTitle>
          <p style={{ color: '#515154', marginBottom: '30px', fontSize: '17px' }}>
            Access utility companies and career opportunities across these countries:
          </p>
          <CountriesGrid>
            <CountryTag>United States</CountryTag>
            <CountryTag>Canada</CountryTag>
            <CountryTag>UK</CountryTag>
            <CountryTag>France</CountryTag>
            <CountryTag>Germany</CountryTag>
            <CountryTag>Australia</CountryTag>
          </CountriesGrid>
        </CountriesSection>
      </FeaturesSection>

      <PromotionSection>
        <SectionTitle>Accelerate Your Career Growth</SectionTitle>
        <FeaturesGrid>
          <PromotionCard>
            <PromotionIcon>📚</PromotionIcon>
            <FeatureTitle>Distribution Power Engineering Fundamentals</FeatureTitle>
            <FeatureDescription>
              Master the essential concepts of distribution power engineering with our comprehensive Udemy course. 
              Perfect for engineers looking to strengthen their technical foundation and advance in utility careers.
            </FeatureDescription>
            <div style={{ marginTop: '20px' }}>
              <ExternalButton 
                href="https://www.udemy.com/course/distribution-power-engineering-fundamentals/?referralCode=C0080B19F713760533B2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Enroll on Udemy
              </ExternalButton>
            </div>
          </PromotionCard>

          <PromotionCard>
            <PromotionIcon>✍️</PromotionIcon>
            <FeatureTitle>Professional Resume Review & Writing</FeatureTitle>
            <FeatureDescription>
              Get your resume optimized for the power utility industry by an experienced professional. 
              I'll help highlight your relevant skills and experience to stand out to utility employers.
            </FeatureDescription>
            <div style={{ marginTop: '20px' }}>
              <ExternalButton 
                href="https://www.fiverr.com/miktech/revise-your-resume-for-the-power-utility-and-energy-industry"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: 'linear-gradient(135deg, #ff375f, #ff2d55)' }}
              >
                Get Resume Help
              </ExternalButton>
            </div>
          </PromotionCard>
        </FeaturesGrid>
      </PromotionSection>
    </PageContainer>
  );
};

export default Home;